/**
 * Sentry configuration hardening (WEB-013 / WL-068).
 *
 * Fixes for the audit finding "Sentry: hardcoded DSN, 100% trace sampling,
 * Replay, no env gating":
 *   - DSN + sampling driven by env (safe defaults; the existing DSN is kept as a
 *     fallback so staging keeps reporting without a new env var — a Sentry DSN is
 *     a public ingest key by design, so this is not a secret leak).
 *   - Trace sampling defaults to 0.1 (10%) instead of 1.0, override via
 *     REACT_APP_SENTRY_TRACES_SAMPLE_RATE.
 *   - Session Replay masks all text + inputs and blocks all media so PII is not
 *     captured; can be disabled entirely via REACT_APP_SENTRY_REPLAY=off.
 *   - `beforeSend` strips auth headers, cookies, tokens and query-string secrets
 *     from every event before it leaves the browser.
 *   - Sentry only initialises when a DSN is present AND not explicitly disabled,
 *     so it stays quiet in local dev by default.
 *
 * Pure functions here are unit-tested in sentryConfig.test.js.
 */

// The DSN previously hardcoded in index.js — retained as the default so existing
// deployments keep reporting. A Sentry DSN is a public client ingest key.
export const DEFAULT_SENTRY_DSN =
  'https://a1c593901b8bce37af791fa3a68b7127@o4505908202242048.ingest.sentry.io/4505919576080384';

// Keys whose values must never be sent to Sentry (case-insensitive match).
const SENSITIVE_KEY_RE =
  /(authorization|cookie|token|password|secret|otp|api[_-]?key|access[_-]?token|refresh[_-]?token|purchase[_-]?token|cvv|card)/i;

// Query-string param names (case-insensitive) whose values get redacted in URLs.
const SENSITIVE_QUERY_RE =
  /^(token|access_token|refresh_token|password|otp|secret|api_key|apikey|code|key)$/i;

const REDACTED = '[Filtered]';

// kelly-1002-P2-segment-open-close-staccato-bounce-addendum-0455: MEASURED live
// on web-dev.iyuga.net (bundle main.a0b1030e.js, CDP Profiler.start/.stop around
// a real segment-close click, 1500ms window) that the dominant SELF-time
// JavaScript cost during an Edit-Wheel segment open/close is NOT the app's own
// render/resize code (createPie's pillOuterRadiusFor: ~6ms) but Sentry Replay's
// rrweb DOM-mutation recorder (handleScope.ts "arg" + normalize.ts "visit":
// >100ms combined per close) walking and serializing every mutation the 8-arc
// SVG redraw produces. Because `replaysOnErrorSampleRate` is > 0, Replay runs
// its mutation observer in "buffer" mode on effectively every session (ready to
// retroactively save on an error), not just the `replaysSessionSampleRate`
// 10% — so this overhead hits far more sessions than the sampling rate implies.
// That main-thread cost (300-650ms observed) delays the synchronous
// useLayoutEffect resize PR#6429 added past the next paint, reproducing the
// exact "stale small size, panel already gone, then a hard pop" staccato bug
// that fix targeted — just caused by instrumentation overhead instead of the
// original async-ResizeObserver race. `block` (rrweb) replaces the matched
// subtree with a static placeholder BEFORE the mutation observer ever attaches
// to it, so none of this recording cost is paid for the wheel canvas; Replay
// still records everything else on the page normally.
//
// T22.452: `.grecaptcha-badge` is the container Google's reCAPTCHA v3 script
// appends to <body> (mounted app-wide, see src/contexts/RecaptchaContext.jsx)
// holding the cross-origin www.google.com anchor iframe. When rrweb serializes
// an <iframe> it calls onceIframeLoaded(), which reads
// iframe.contentWindow.document.readyState inside a try/catch
// (@sentry/replay cjs onceIframeLoaded). The catch hides the throw, but WebKit
// still logs "Blocked a frame with origin ... from accessing a frame with
// origin https://www.google.com" for every such read. Playwright WebKit
// reports that as a pageerror, so every QA audit of /login on WebKit fails.
// MEASURED web-stg main.dfc84151 /login 1440: 2/2 with it, 0/2 with this
// selector added, and login still reaches /discover. rrweb never serializes
// the children of a blocked node, so the iframe is never touched. The
// third-party iframe could never be recorded anyway.
export const REPLAY_BLOCKED_SELECTORS = [
  '[data-testid="builder-wheel-fit-area"]',
  '.grecaptcha-badge',
];

// WebKit/Safari logs a cross-origin frame-access error for the reCAPTCHA v3
// badge iframe (mounted app-wide, see src/contexts/RecaptchaContext.jsx).
// T22.452 traced the Replay-recorder source of it (blocked above). Keep this
// filter for any other probe that still trips it. Chromium and Firefox do not throw this (MEASURED live on
// web-dev.iyuga.net 2026-09-25: 0 occurrences on chromium/firefox across the
// full golden path, 100% occurrence on webkit on every page). The error
// originates from Google's separately-hosted recaptcha__en.js, not app code,
// and does not affect functionality (title-save, wedge rendering, and login
// all still succeed when it fires) -- it is pure Sentry noise for real Safari
// users and would otherwise fire on every WebKit pageview app-wide.
const IGNORED_ERROR_PATTERNS = [
  /Blocked a frame with origin .* from accessing a frame with origin "https:\/\/www\.google\.com"/,
];

function redactUrl(url) {
  if (typeof url !== 'string' || url.indexOf('?') === -1) return url;
  const [base, query] = url.split('?');
  const scrubbed = query
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      const name = eq === -1 ? pair : pair.slice(0, eq);
      return SENSITIVE_QUERY_RE.test(decodeURIComponent(name))
        ? `${name}=${REDACTED}`
        : pair;
    })
    .join('&');
  return `${base}?${scrubbed}`;
}

function redactHeaders(headers) {
  if (!headers || typeof headers !== 'object') return headers;
  const out = Array.isArray(headers) ? [...headers] : { ...headers };
  Object.keys(out).forEach((k) => {
    if (SENSITIVE_KEY_RE.test(k)) out[k] = REDACTED;
  });
  return out;
}

/**
 * Recursively redact sensitive values from a request-body-like object.
 * Bounded depth to avoid pathological / cyclic structures.
 */
function redactData(value, depth) {
  if (depth > 6 || value == null) return value;
  if (Array.isArray(value)) return value.map((v) => redactData(v, depth + 1));
  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((k) => {
      out[k] = SENSITIVE_KEY_RE.test(k)
        ? REDACTED
        : redactData(value[k], depth + 1);
    });
    return out;
  }
  return value;
}

/**
 * Sentry `beforeSend` hook: strip PII / credentials from an event in place-safe
 * fashion (returns a scrubbed copy-ish; mutates nested request object which is
 * fine for a terminal send hook). Never throws — a scrub failure must not lose
 * the error report, but must also never send raw secrets, so on error we drop
 * the request payload entirely.
 */
export function scrubEvent(event) {
  if (!event || typeof event !== 'object') return event;
  try {
    if (event.request && typeof event.request === 'object') {
      const req = event.request;
      if (req.headers) req.headers = redactHeaders(req.headers);
      if (req.cookies) req.cookies = REDACTED;
      if (req.url) req.url = redactUrl(req.url);
      if (req.query_string && typeof req.query_string === 'string') {
        req.query_string = redactUrl(`?${req.query_string}`).slice(1);
      }
      if (req.data !== undefined) req.data = redactData(req.data, 0);
    }
    // Keep only a non-PII user id; drop email / ip / username.
    if (event.user && typeof event.user === 'object') {
      event.user = event.user.id != null ? { id: event.user.id } : {};
    }
  } catch (e) {
    // Fail closed on the payload: drop request data rather than risk leaking it.
    if (event.request) event.request = { url: REDACTED };
  }
  return event;
}

function envValue(env, key) {
  return env && typeof env === 'object' ? env[key] : undefined;
}

function parseRate(raw, fallback) {
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback;
}

/**
 * Build the Sentry.init options object from the environment, or return null when
 * Sentry should not initialise (no DSN, or explicitly disabled).
 *
 * @param {object} env  a `process.env`-like object.
 * @param {object} deps injected Sentry pieces so this stays unit-testable:
 *                      { BrowserTracing, Replay }.
 */
export function buildSentryOptions(env, deps = {}) {
  const disabled = String(envValue(env, 'REACT_APP_SENTRY_ENABLED')).toLowerCase() === 'false';
  if (disabled) return null;

  const dsn = envValue(env, 'REACT_APP_SENTRY_DSN') || DEFAULT_SENTRY_DSN;
  if (!dsn) return null;

  const tracesSampleRate = parseRate(
    envValue(env, 'REACT_APP_SENTRY_TRACES_SAMPLE_RATE'),
    0.1,
  );
  const replaysSessionSampleRate = parseRate(
    envValue(env, 'REACT_APP_SENTRY_REPLAY_SESSION_RATE'),
    0.1,
  );
  const replaysOnErrorSampleRate = parseRate(
    envValue(env, 'REACT_APP_SENTRY_REPLAY_ERROR_RATE'),
    1.0,
  );
  const replayOff =
    String(envValue(env, 'REACT_APP_SENTRY_REPLAY')).toLowerCase() === 'off';

  const integrations = [];
  if (deps.BrowserTracing) {
    integrations.push(
      new deps.BrowserTracing({
        tracePropagationTargets: ['localhost', /^https:.*.io\/api/],
      }),
    );
  }
  if (!replayOff && deps.Replay) {
    integrations.push(
      new deps.Replay({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        block: REPLAY_BLOCKED_SELECTORS,
      }),
    );
  }

  const environment =
    envValue(env, 'REACT_APP_SENTRY_ENV') ||
    envValue(env, 'NODE_ENV') ||
    'production';

  return {
    dsn,
    environment,
    integrations,
    tracesSampleRate,
    replaysSessionSampleRate: replayOff ? 0 : replaysSessionSampleRate,
    replaysOnErrorSampleRate: replayOff ? 0 : replaysOnErrorSampleRate,
    sendDefaultPii: false,
    beforeSend: scrubEvent,
    ignoreErrors: IGNORED_ERROR_PATTERNS,
  };
}
